import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadECMRFromXml, saveECMR } from '../services/eCMRService';

const ImportECMR: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setError(null);
      setValidationWarnings([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Por favor, seleccione un archivo XML');
      return;
    }

    setLoading(true);
    setError(null);
    setValidationWarnings([]);

    try {
      const { ecmr, validationResult } = await loadECMRFromXml(file);
      
      // Mostrar advertencias de validación si las hay
      if (validationResult && !validationResult.valid && validationResult.errors) {
        setValidationWarnings(validationResult.errors);
      }
      
      const key = saveECMR(ecmr);
      
      // Si hay advertencias pero no es un error crítico, mostramos las advertencias
      // pero igualmente permitimos continuar
      if (validationWarnings.length > 0) {
        navigate(`/view/${key}`, { 
          state: { validationWarnings } 
        });
      } else {
        navigate(`/view/${key}`);
      }
    } catch (err) {
      setError(`Error al importar el archivo: ${(err as Error).message}`);
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Importar e-CMR desde XML</h1>
      
      <div className="bg-white shadow-md rounded p-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="xml-file">
              Archivo XML
            </label>
            <input
              type="file"
              id="xml-file"
              accept=".xml"
              onChange={handleFileChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {error && (
              <p className="text-red-500 text-xs italic mt-2">{error}</p>
            )}
          </div>
          
          {validationWarnings.length > 0 && (
            <div className="mb-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
              <h3 className="font-bold">Advertencias de validación:</h3>
              <ul className="list-disc pl-5">
                {validationWarnings.map((warning, index) => (
                  <li key={index} className="text-sm">{warning}</li>
                ))}
              </ul>
              <p className="text-sm mt-2">Puedes continuar a pesar de estas advertencias, pero el documento podría no ser completamente compatible.</p>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={loading || !file}
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                (loading || !file) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Importando...' : 'Importar'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportECMR; 